import { Box, Grid, Typography, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import Paper from 'components/paper';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedDate, FormattedNumber, useIntl } from 'react-intl';
import { FishGroup } from 'views/planning-prognosis/features/view-fish-group/types/fish-group.type';
import { postFishGroupStrain } from '../api/fish-group-stocking/create-fish-group-strain';
import { getFishGroupStrains } from '../api/get-fish-group-strains';

type FormValues = {
  seaCloudStrainId: number;
};

type Props = {
  fishGroup: FishGroup;
  isEditable: boolean;
};

const FishGroupStocking: React.FC<Props> = ({ fishGroup, isEditable }) => {
  const intl = useIntl();
  const snackbarDispatch = useSnackbarDispatch();
  const { account } = useAccountState();
  const theme = useTheme();

  // #region FORMS

  const { handleSubmit, control, watch } = useForm<FormValues>({
    defaultValues: useMemo(() => {
      return {
        seaCloudStrainId: fishGroup.seaCloudStrainId ?? '',
      };
    }, [fishGroup]),
    shouldUnregister: true,
  });

  // #endregion FORMS

  // #region FETCH DATA

  const { data: strains } = useGet(
    ['getFishGroupStrains', account.company?.marketId, fishGroup?.fishGroupId],
    (context) => getFishGroupStrains(Number(context.queryKey[1])),
    {
      enabled: !!account.company?.marketId && !!fishGroup?.fishGroupId,
    }
  );

  // #endregion FETCH DATA

  // #region POST DATA

  const updateStrain = usePost<AxiosResponse, { globalSiteId: string; fishGroupId: string; seaCloudStrainId: number }>(
    (request) => postFishGroupStrain(request.globalSiteId, request.fishGroupId, request.seaCloudStrainId),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // const updateComment = usePost<AxiosResponse, { globalSiteId: string; fishGroupId: string; comment: string }>(
  //   (request) => postPlanAndPrognosisFishGroupComment(request.globalSiteId, request.fishGroupId, request.comment),
  //   () => {
  //     snackbarDispatch({
  //       type: SnackbarActionTypes.SUCCESS_SNACKBAR,
  //       payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
  //     });
  //   },
  //   (error) => {
  //     snackbarDispatch({
  //       type: SnackbarActionTypes.ERROR_SNACKBAR,
  //       payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
  //     });
  //   }
  // );

  // #endregion POST DATA

  // #region HANDLERS

  const handleUpdateStrain = (data: FormValues) => {
    if (!!fishGroup) {
      updateStrain.mutate({
        globalSiteId: fishGroup.globalSiteId,
        fishGroupId: fishGroup.fishGroupId,
        seaCloudStrainId: data.seaCloudStrainId,
      });
    }
  };

  // const handleUpdateComment = (data: FormValues) => {
  //   if (!!fishGroup) {
  //     updateComment.mutate({
  //       globalSiteId: fishGroup.siteGlobalId,
  //       fishGroupId: fishGroup.fishGroupId,
  //       comment: data.comment,
  //     });
  //   }
  // };

  // #endregion HANDLERS

  return (
    <>
      <Paper transparent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={5}>
          <Typography variant="h3" color={theme.palette.common.white}>
            Fish Group
          </Typography>
        </Box>
        <Paper>
          <Grid container rowGap={4} spacing={4}>
            <Grid size={6}>
              <Typography variant="caption">Stocking date</Typography>
              <Typography gutterBottom>
                <FormattedDate value={fishGroup.stockingDate} day="2-digit" month="short" year="numeric" />
              </Typography>
              <Typography variant="caption">Number of fish</Typography>
              <Typography gutterBottom>
                <FormattedNumber value={fishGroup.stockingNumber} />
              </Typography>
              <Typography variant="caption">Stocking weight</Typography>
              <Typography gutterBottom>
                <FormattedNumber
                  value={fishGroup.stockingWeight}
                  style={`unit`}
                  unit="gram"
                  minimumFractionDigits={0}
                  maximumFractionDigits={0}
                />
              </Typography>
              <Typography variant="caption">Species</Typography>
              <Typography gutterBottom>{fishGroup.speciesName}</Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="caption">Last production date</Typography>
              <Typography gutterBottom>
                <FormattedDate value={fishGroup.lastPeriodRec} day="2-digit" month="short" year="numeric" />
              </Typography>
              <Typography variant="caption">Number of fish</Typography>
              <Typography gutterBottom>
                <FormattedNumber value={fishGroup.lastNum} />
              </Typography>
              <Typography variant="caption">Latest weight</Typography>
              <Typography>
                <FormattedNumber
                  value={(fishGroup.lastBiom / fishGroup.lastNum) * 1000}
                  style={`unit`}
                  unit="gram"
                  minimumFractionDigits={0}
                  maximumFractionDigits={0}
                />
              </Typography>
              <Typography variant="caption">Smolt type</Typography>
              <Typography>{fishGroup.smoltName}</Typography>
            </Grid>
            <Grid size={12}>
              <Select
                label="Strain"
                fullWidth
                dark
                disabled={!isEditable}
                controller={{
                  name: 'seaCloudStrainId',
                  control: control,
                }}
                onChange={() => handleSubmit(handleUpdateStrain)()}>
                {strains?.map((s) => (
                  <RadioSelectItem key={s.id} value={s.id} selected={s.id === watch('seaCloudStrainId')}>
                    {s.name}
                  </RadioSelectItem>
                ))}
              </Select>
            </Grid>
            {/* <Grid size={12}>
            <Typography variant="caption">Comment</Typography>
            <TextField
              hiddenLabel
              multiline
              margin="none"
              label="comment"
              disabled={!isEditable}
              controller={{
                name: 'comment',
                control: control,
              }}
            />
            <Box display="flex" justifyContent="flex-end">
              <Mui.FormControl margin="dense">
                <Button disabled={!isEditable} size="small" onClick={() => handleSubmit(handleUpdateComment)()}>
                  Save Comment
                </Button>
              </Mui.FormControl>
            </Box>
          </Grid> */}
          </Grid>
        </Paper>
      </Paper>
    </>
  );
};

export default FishGroupStocking;
